import { effect, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from './environment.service';
import { ICreativeset } from '@domain/creativeset';

type LastAccessedSet = {
    id: string;
    name: string;
    url: string;
};

/**
 * Stores/Loads the last 5 creative sets from a cookie
 * Only works when under .studio-sandbox.pages.dev domain
 *
 * Ment to improve DX
 */
@Injectable({
    providedIn: 'root'
})
export class LastAccessedSetsService {
    private readonly environmentService = inject(EnvironmentService);

    lastAccessedSets = signal<LastAccessedSet[]>([]);

    private readonly storageKey = 'Studio-LastAccessedSets';
    private readonly supportedDomains = ['.studio-sandbox.pages.dev', 'remote-studio.bannerflow.com'];

    constructor() {
        if (this.isServiceEnabled()) {
            this.loadLastAccessedSets();
        }

        effect(() => {
            const lastAccessedSets = this.lastAccessedSets();
            const stringifiedValue = JSON.stringify(lastAccessedSets);
            for (const domain of this.supportedDomains) {
                this.setCookie(this.storageKey, stringifiedValue, 15, '/', domain, true, 'None');
            }
        });
    }

    addSetToList(creativeSet: ICreativeset): void {
        if (!this.isServiceEnabled()) {
            return;
        }

        const lastAccessedSets = this.lastAccessedSets();

        this.lastAccessedSets.set([
            {
                id: creativeSet.id,
                name: creativeSet.name,
                url: `/brand/${creativeSet.brandId}/creativeset/${creativeSet.id}`
            },
            ...lastAccessedSets.filter(({ id }) => id !== creativeSet.id).slice(0, 4)
        ]);
    }

    private loadLastAccessedSets(): void {
        const storedValue = this.getCookie(this.storageKey);

        try {
            const parsed = JSON.parse(storedValue ?? 'null');
            if (parsed) {
                this.lastAccessedSets.set(parsed);
            }
        } catch {
            this.lastAccessedSets.set([]);
        }
    }

    private setCookie(
        name: string,
        value: string,
        days: number,
        path: string,
        domain: string,
        secure: boolean,
        sameSite: 'Strict' | 'Lax' | 'None'
    ): void {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = `; expires=${date.toUTCString()}`; // Format date for expires
        }
        const pathAttr = path ? `; path=${path}` : '';
        const domainAttr = domain ? `; domain=${domain}` : ''; // Add domain attribute
        const secureAttr = secure ? '; secure' : '';
        const sameSiteAttr = sameSite ? `; SameSite=${sameSite}` : '';

        document.cookie = `${name}=${encodeURIComponent(value)}${expires}${pathAttr}${domainAttr}${secureAttr}${sameSiteAttr}`;
    }

    private getCookie(name: string): string | undefined {
        // Add '=' to the name to ensure we match the full cookie name
        const nameEQ = `${name}=`;
        // Split document.cookie on semicolons to get an array of cookies
        const ca = document.cookie.split(';');

        // Loop through the cookie array
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            // Remove leading whitespace (e.g., the space after the semicolon)
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            // If this cookie string begins with the name we want...
            if (c.indexOf(nameEQ) === 0) {
                // ...return the decoded value (the rest of the string after the name and '=')
                return decodeURIComponent(c.substring(nameEQ.length, c.length));
            }
        }
        // If the loop finishes without finding the cookie, return null
        return undefined;
    }

    private isServiceEnabled(): boolean {
        return (
            (this.environmentService.appEnvironment.stage === 'sandbox' ||
                this.environmentService.appEnvironment.stage === 'local') &&
            this.supportedDomains.some(domain => window.location.href.includes(domain))
        );
    }
}
